import { Header } from "@/components/Layout/Header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useEvents } from "@/hooks/useEvents";
import { useTeams } from "@/hooks/useTeams";
import {
  EVENT_STATUSES,
  EVENT_STATUS_LABELS,
  EVENT_STATUS_COLORS,
} from "@/utils/constants";
import { cn } from "@/lib/utils";

export function ReportsPage() {
  const { data: events = [] } = useEvents();
  const { data: teams = [] } = useTeams();

  const statusCounts = EVENT_STATUSES.reduce(
    (acc, status) => {
      acc[status] = events.filter((e) => e.status === status).length;
      return acc;
    },
    {} as Record<string, number>,
  );

  const totalBudget = events.reduce(
    (sum, e) => sum + (e.budget_estimate || 0),
    0,
  );

  const teamUtilization = teams.map((team) => ({
    ...team,
    eventCount: events.filter((e) => e.team_id === team.id).length,
  }));

  const upcomingEvents = events
    .filter(
      (e) =>
        new Date(e.event_date) >= new Date() &&
        !["Completed", "Abandoned"].includes(e.status),
    )
    .slice(0, 5);

  return (
    <div className="flex h-full flex-col">
      <Header title="Rapports" />

      <div className="flex-1 space-y-6 p-4 md:p-6">
        <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs md:text-sm">
                Total événements
              </CardDescription>
              <CardTitle className="text-2xl md:text-3xl">
                {events.length}
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs md:text-sm">
                En cours
              </CardDescription>
              <CardTitle className="text-2xl md:text-3xl">
                {
                  events.filter(
                    (e) => !["Completed", "Abandoned"].includes(e.status),
                  ).length
                }
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs md:text-sm">
                Budget total estimé
              </CardDescription>
              <CardTitle className="text-2xl md:text-3xl">
                {totalBudget.toLocaleString()}€
              </CardTitle>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs md:text-sm">
                Équipes actives
              </CardDescription>
              <CardTitle className="text-2xl md:text-3xl">
                {teams.filter((t) => t.is_active).length}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Pipeline par statut</CardTitle>
              <CardDescription>
                Répartition des événements par étape
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {EVENT_STATUSES.map((status) => (
                  <div
                    key={status}
                    className="flex items-center gap-2 md:gap-3"
                  >
                    <div
                      className={cn(
                        "h-3 w-3 shrink-0 rounded-full",
                        EVENT_STATUS_COLORS[status],
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate text-xs md:text-sm">
                      {EVENT_STATUS_LABELS[status]}
                    </span>
                    <span className="shrink-0 font-medium">
                      {statusCounts[status]}
                    </span>
                    <div className="hidden h-2 w-16 overflow-hidden rounded-full bg-muted sm:block md:w-24">
                      <div
                        className={cn("h-full", EVENT_STATUS_COLORS[status])}
                        style={{
                          width: `${events.length > 0 ? (statusCounts[status] / events.length) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Utilisation des équipes</CardTitle>
              <CardDescription>
                Nombre d&apos;événements par équipe
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {teamUtilization
                  .sort((a, b) => b.eventCount - a.eventCount)
                  .map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center gap-2 md:gap-3"
                    >
                      <span className="min-w-0 flex-1 truncate text-xs md:text-sm">
                        {team.name}
                      </span>
                      <span className="shrink-0 font-medium">
                        {team.eventCount}
                      </span>
                      <div className="hidden h-2 w-16 overflow-hidden rounded-full bg-muted sm:block md:w-24">
                        <div
                          className="h-full bg-primary"
                          style={{
                            width: `${events.length > 0 ? (team.eventCount / events.length) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                {teamUtilization.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Aucune équipe disponible
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Prochains événements</CardTitle>
            <CardDescription>
              Les 5 prochains événements à venir
            </CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingEvents.length > 0 ? (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div>
                      <p className="font-medium">{event.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {event.location}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {new Date(event.event_date).toLocaleDateString("fr-FR")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {EVENT_STATUS_LABELS[event.status]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Aucun événement à venir
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
